<script>
import { mapActions, mapGetters } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'
import difference from 'lodash/difference'
import isPast from '@lib/utils/date-is-past'
import dateIsAfter from '@lib/utils/date-is-after'
import ValidationMixin from '@lib/mixins/validation'
import { mask } from 'vue-the-mask'
import { datePickerFormat } from '@lib/utils/date-helper'

const initialValues = {
  enabled: true,
  endsAt: null,
  name: '',
  redemptionLimit: null,
  offers: [],
  startsAt: null,
  tags: [],
  providerId: null,
}

export default {
  name: 'OfferGroupForm',
  page: {
    title: 'Offer Group Form',
  },
  directives: {
    mask,
  },
  mixins: [ValidationMixin],
  data() {
    return {
      form: { ...initialValues },
      inputTagValue: '',
      datePickerFormat,
      progress: false,
      rules: {
        providerId: {
          required: true,
          message: 'Provider name is required',
        },
        name: [
          {
            required: true,
            message: 'Offer group name is required',
          },
          {
            max: 60,
            message: 'Offer group name must be 60 characters or less',
          },
        ],
        redemptionLimit: {
          required: true,
          message: 'Redemption limit is required',
        },
        startsAt: {
          required: true,
          message: 'Start date is required',
        },
        endsAt: {
          required: true,
          message: 'End date is required',
        },
      },
      offers: [],
      offersOptions: [],
      showDeleteDialog: false,
    }
  },

  computed: {
    ...mapGetters('me', [
      'canDeleteOffer',
      'providerId',
      'isSuperAdmin',
    ]),
    ...mapGetters('offerGroup', [
      'getOfferGroupFromState',
    ]),
    ...mapGetters('providers', [
      'providers',
    ]),

    isEdit() {
      return Boolean(this.$route.params.id)
    },
    enabled() {
      return this.form.enabled ? 'Active' : 'Inactive'
    },
    isTagsChanged() {
      if (!this.isEdit) return true
      const { tags } = this.form
      if (tags.length !== this.getOfferGroupFromState.tags.length) return true
      return Boolean(difference(this.getOfferGroupFromState.tags, tags).length)
    },
    isOffersChanged() {
      if (!this.isEdit) return true
      const { offers } = this.form
      if (offers.length !== this.getOfferGroupFromState.offers.length) return true
      return Boolean(difference(this.getOfferGroupFromState.offers, offers).length)
    },
    isDataOfferChanged() {
      if (!this.isEdit) return true
      // eslint-disable-next-line no-unused-vars
      const { tags, offers, ...rest } = this.form
      return !Object.keys(rest).every(key => {
        switch (key) {
          case 'redemptionLimit': return Number(rest[key]) === this.getOfferGroupFromState[key]
          case 'startsAt':
          case 'endsAt': return rest[key].slice(0, 10) === this.getOfferGroupFromState[key].slice(0, 10)
          default: return rest[key] === this.getOfferGroupFromState[key]
        }
      })
    },
    isSaveBtnDisabled() {
      const isFormChanged = this.isOffersChanged || this.isTagsChanged || this.isDataOfferChanged
      return (this.isEdit && !isFormChanged) || this.progress
    },
    providersOptions() {
      return this.providers.length ? this.providers.map(provider => ({
        label: provider.name,
        value: provider.id,
      })) : []
    },

    startDatePickerOptions() {
      const { endsAt } = this.form

      return {
        disabledDate(date) {
          return endsAt ? dateIsAfter(date, endsAt) : false
        },
      }
    },

    endDatePickerOptions() {
      const { startsAt } = this.form

      return {
        disabledDate(date) {
          let isBeforeStartDate = false

          if (startsAt) {
            isBeforeStartDate = dateIsAfter(startsAt, date)
          }

          return isPast(date, true) || isBeforeStartDate
        },
      }
    },
  },

  watch: {
    'form.providerId'(value) {
      if (!this.offers.length) return
      this.form.offers = []
      this.offersOptions = this.offers.reduce((acc, offer) => {
        if (offer.providerId === value) {
          return acc.concat({
            label: offer.name,
            value: `offers/${offer.id}`,
          })
        }
        return acc
      }, [])
    },
    providers: {
      handler(val) {
        if (this.providers.length === 1) {
          this.form.providerId = this.providers[0].id
        }
      },
    },
    deep: true,
  },

  mounted() {
    this.getOffers()

    if (this.isEdit) {
      this.form = cloneDeep({ ...this.getOfferGroupFromState })
    }

    if (this.providers.length === 1) {
      this.form.providerId = this.providers[0].id
    }
  },

  methods: {
    ...mapActions('offerGroup', [
      'createOfferGroup',
      'updateOfferGroup',
      'deleteOfferGroup',
      'getAllOffers',
    ]),
    async getOffers() {
      const [, offers] = await this.getAllOffers()
      this.offers = offers.items
      this.offersOptions = this.offers.map(offer => ({
        value: `offers/${offer.id}`,
        label: offer.name,
      }))
    },

    handleClose(tag) {
      this.form.tags.splice(this.form.tags.indexOf(tag), 1)
    },

    handleInputConfirm() {
      if (this.inputTagValue) {
        this.form.tags.push(this.inputTagValue)
      }
      this.inputTagValue = ''
    },

    onCancel() {
      this.$nextTick(() => {
        this.$refs.form.clearValidate()
      })
      const params = {
        name: this.isEdit ? 'offer-group-details' : 'offer-groups',
        ...this.isEdit && { params: { id: this.$route.params.id } },
      }
      this.$router.push(params)
    },

    async onSubmit() {
      this.clearBackendErrors()
      if (this.isSaveBtnDisabled) return

      const formValid = await this.$refs.form.validate().catch(() => false)
      if (!formValid) return

      this.progress = true
      const { providerId, ...rest } = this.form
      const options = { headers: {
        'x-provider-id': providerId,
        'Content-Type': 'application/json',
      } }
      const data = {
        ...rest,
        redemptionLimit: Number(rest.redemptionLimit),
      }
      const response = await this[this.isEdit ? 'updateOfferGroup' : 'createOfferGroup']({
        data,
        options,
      })

      let [error, success] = response
      let errorMsg = null

      if (error === undefined && !success) {
        error = {}
        errorMsg = 'Something went wrong'
      }

      if (error) {
        this.processBackendErrors(error)
      }

      this.$notifier({
        response: [error, success],
        errorTitle: 'Unable to save offer group',
        successTitle: 'Offer group saved',
        successMsg: `${this.isEdit ? 'Changes' : 'New offer group'} successfully saved.`,
        ...errorMsg && { errorMsg },
      })

      if (success) {
        this.$router.push({
          name: 'offer-group-details',
          params: { id: success.id },
        })
      }

      this.progress = false
    },

    async onDelete() {
      this.showDeleteDialog = false
      this.progress = true

      await this.deleteOfferGroup('248f269f-76cb-44d4-9b38-fc96e97cc9c6').then(response => {
        this.$notifier({
          response,
          errorTitle: 'Unable to delete offer group',
          successTitle: 'Offer group deleted',
          successMsg: 'Offer group successfully deleted.',
        }).then(success => {
          if (success) {
            this.$router.push({
              name: 'offer-groups',
            })
          }
        })
      })

      this.progress = false
    },
  },
}
</script>
<template>
  <div>
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      label-position="top"
      :disabled="progress"
      class="card-form"
    >
      <el-form-item
        label="Select Provider"
        prop="providerId"
      >
        <el-select
          v-model="form.providerId"
          :disabled="providersOptions.length === 1"
          filterable
          allow-create
          default-first-option
          placeholder="Choose Provider"
        >
          <el-option
            v-for="item in providersOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item
        label="Enabled"
        prop="enabled"
      >
        <el-switch
          v-model="form.enabled"
          active-color="#13ce66"
          inactive-color="#a7a7a7"
          :active-value="true"
          :inactive-value="false"
        />
        <span :class="$style.enabled">
          {{ enabled }}
        </span>
      </el-form-item>

      <el-form-item
        label="Offer Group Name"
        prop="name"
        :error="backendErrors.name"
      >
        <el-input v-model="form.name" />
      </el-form-item>

      <el-form-item
        label="Redemption limit"
        prop="redemptionLimit"
        :error="backendErrors.redemptionLimit"
      >
        <el-input
          v-model="form.redemptionLimit"
          placeholder="Enter redemption limit"
          type="number"
        />
      </el-form-item>


      <el-form-item
        label="Offer Group Start Date"
        prop="startsAt"
      >
        <el-date-picker
          v-model="form.startsAt"
          v-mask="['##/##/####']"
          type="date"
          format="dd/MM/yyyy"
          :value-format="datePickerFormat"
          placeholder="Enter Date"
          :picker-options="startDatePickerOptions"
        />
      </el-form-item>

      <el-form-item
        label="Offer Group EndsAt Date"
        prop="endsAt"
      >
        <el-date-picker
          v-model="form.endsAt"
          v-mask="['##/##/####']"
          type="date"
          format="dd/MM/yyyy"
          :value-format="datePickerFormat"
          placeholder="Enter Date"
          :picker-options="endDatePickerOptions"
        />
      </el-form-item>

      <el-form-item
        label="Add Offers in Group"
      >
        <el-select
          v-model="form.offers"
          v-loading="!offers.length"
          multiple
          filterable
          allow-create
          default-first-option
          :disabled="!form.providerId"
          :placeholder="form.providerId ? 'Choose offers' : 'Choose provider first' "
        >
          <el-option
            v-for="item in offersOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>

      <!-- Tags -->
      <el-form-item
        label="Tags"
      >
        <el-input
          v-model="inputTagValue"
          placeholder="Enter tags"
          @keyup.enter.native="handleInputConfirm"
          @blur="handleInputConfirm"
        />
      </el-form-item>

      <div :class="$style.tags">
        <el-tag
          v-for="(tag, index) in form.tags"
          :key="`${tag}${index}`"
          :class="$style.tag"
          :closable="!progress"
          :disable-transitions="false"
          disabled
          @close="handleClose(tag)"
        >
          {{ tag }}
        </el-tag>
      </div>

      <div
        v-if="offers.length"
        class="card-form__footer"
      >
        <div>
          <el-button
            :disabled="progress"
            @click="onCancel"
          >
            Cancel
          </el-button>
          <el-button
            type="primary"
            :disabled="isSaveBtnDisabled"
            @click="onSubmit"
          >
            Save Offer group
          </el-button>
        </div>
      </div>
    </el-form>
  </div>
</template>

<style lang="scss" module>
  .enabled {
    margin-left: rem(14px);
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
  }

  .tag {
    margin-right: 10px;
    margin-bottom: 10px;
  }

  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type='number'] {
    -moz-appearance: textfield;
  }

  .msg {
    text-align: center;
    white-space: nowrap;
  }
</style>
